import { useState, useEffect } from "react";
import {
  Search,
  Filter,
  UserPlus,
  Edit,
  Trash2,
  UserX,
  UserCheck,
  Eye,
} from "lucide-react";
import Card from "../components/Card";
import Table from "../components/Table";
import Button from "../components/Button";
import Badge from "../components/Badge";
import Pagination from "../components/Pagination";
import Modal from "../components/Modal";
import useUser from "../hooks/useUsers";
import { useToast } from "../components/Toast";

const Users = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({ country: "", disabled: "" });
  const [formData, setFormData] = useState({
    email: "",
    country: "",
    city: "",
    region: "",
    disabled: false,
  });
  const { loading, user, getAllUsers } = useUser();
  const { addToast } = useToast();
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch users from API
  useEffect(() => {
    getAllUsers(currentPage, pageSize);
    // eslint-disable-next-line
  }, [currentPage, pageSize]);

  // Filter and search logic
  useEffect(() => {
    let result = Array.isArray(user) ? user : user?.data || [];
    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (u) =>
          u.email?.toLowerCase().includes(search) ||
          (u.country && u.country.toLowerCase().includes(search)) ||
          (u.city && u.city.toLowerCase().includes(search)) ||
          (u.region && u.region.toLowerCase().includes(search))
      );
    }
    // Apply country filter
    if (filters.country) {
      result = result.filter((u) => u.country === filters.country);
    }
    // Apply disabled filter
    if (filters.disabled !== "") {
      const isDisabled = filters.disabled === "true";
      result = result.filter((u) => u.disabled === isDisabled);
    }
    setFilteredUsers(result);
    setTotalPages(user?.pagination?.pages || 1);
    setDisplayedUsers(result);
  }, [user, searchTerm, filters]);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already applied via useEffect
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setShowFilters(false);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      country: user.country || "",
      city: user.city || "",
      region: user.region || "",
      disabled: user.disabled || false,
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleToggleUserStatus = (user) => {
    addToast("User status toggling not implemented", "info");
  };

  const handleSaveUser = (e) => {
    e.preventDefault();
    addToast("User editing not implemented", "info");
    setIsEditModalOpen(false);
  };

  const handleConfirmDelete = () => {
    addToast("User deletion not implemented", "info");
    setIsDeleteModalOpen(false);
  };

  // Get unique countries for filter dropdown
  const countries = Array.isArray(user)
    ? [...new Set(user.filter((u) => u.country).map((u) => u.country))].sort()
    : Array.isArray(user?.data)
    ? [
        ...new Set(user.data.filter((u) => u.country).map((u) => u.country)),
      ].sort()
    : [];

  const columns = [
    {
      header: "#",
      accessor: "row_number",
      cell: (row) => (
        <span>
          {displayedUsers.indexOf(row) + 1 + (currentPage - 1) * pageSize}
        </span>
      ),
    },
    {
      header: "Email",
      accessor: "email",
      cell: (row) => (
        <div className="flex items-center">
          <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-600 font-medium">
              {row.email.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="ml-4">
            <div className="font-medium text-gray-900">{row.email}</div>
            <div className="text-gray-500 text-xs">
              {row.city && row.country
                ? `${row.city}, ${row.country}`
                : row.country || "No location"}
            </div>
          </div>
        </div>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      cell: (row) => (
        <div className="space-y-1">
          {row.is_admin && <Badge variant="purple">Admin</Badge>}
          <Badge variant={row.is_verified ? "green" : "red"}>
            {row.is_verified ? "Verified" : "Unverified"}
          </Badge>
        </div>
      ),
    },
    {
      header: "Posts",
      accessor: "posts",
      cell: (row) => (
        <span className="text-gray-700">
          {row.posts ? `${row.posts} posts` : "No posts"}
        </span>
      ),
    },
    {
      header: "Created",
      accessor: "created_at",
      cell: (row) => (
        <span className="text-gray-500">
          {new Date(row.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      cell: (row) => (
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            icon={Eye}
            onClick={(e) => {
              e.stopPropagation();
              handleViewUser(row);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            icon={Edit}
            onClick={(e) => {
              e.stopPropagation();
              handleEditUser(row);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            icon={row.disabled ? UserCheck : UserX}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleUserStatus(row);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            icon={Trash2}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteUser(row);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage user accounts and permissions
        </p>
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row justify-between mb-4">
          <form onSubmit={handleSearch} className="flex mb-4 sm:mb-0">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search users..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button type="submit" variant="outline" className="ml-2">
              Search
            </Button>
            <Button
              type="button"
              variant="outline"
              icon={Filter}
              className="ml-2"
              onClick={() => setShowFilters(!showFilters)}
            >
              Filter
            </Button>
          </form>
          <Button icon={UserPlus}>Add User</Button>
        </div>

        {showFilters && (
          <div className="mb-4 p-4 border border-gray-200 rounded-md bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Filters</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <select
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  onChange={(e) =>
                    handleFilterChange({ country: e.target.value || "" })
                  }
                  value={filters.country}
                >
                  <option value="">All Countries</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  onChange={(e) =>
                    handleFilterChange({ disabled: e.target.value })
                  }
                  value={filters.disabled}
                >
                  <option value="">All Status</option>
                  <option value="false">Active</option>
                  <option value="true">Disabled</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center mb-4">
          <label className="mr-2 text-sm font-medium text-gray-700">
            Page Size:
          </label>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="px-2 py-1 border border-gray-300 rounded-md"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={150}>150</option>
            <option value={200}>200</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
        </div>

        <Table
          columns={columns}
          data={displayedUsers}
          isLoading={loading}
          onRowClick={handleViewUser}
          emptyState="No users found. Try adjusting your search or filters."
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages - 1}
          onPageChange={goToPage}
        />
      </Card>

      {/* View User Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="User Details"
        size="md"
        footer={
          <Button variant="outline" onClick={() => setIsViewModalOpen(false)}>
            Close
          </Button>
        }
      >
        {selectedUser && (
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center text-xl font-medium text-gray-600">
                {selectedUser.email.charAt(0).toUpperCase()}
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedUser.email}
                </h3>
                <p className="text-sm text-gray-500">
                  User ID: {selectedUser.id}
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {selectedUser.email}
                  </dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    Created At
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(selectedUser.created_at).toLocaleString()}
                  </dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    Admin Status
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <Badge variant={selectedUser.is_admin ? "purple" : "gray"}>
                      {selectedUser.is_admin ? "Admin" : "Regular User"}
                    </Badge>
                  </dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    Account Status
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <Badge variant={selectedUser.is_verified ? "green" : "red"}>
                      {selectedUser.is_verified ? "Verified" : "Unverified"}
                    </Badge>
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">
                    Location
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {selectedUser.city &&
                    selectedUser.region &&
                    selectedUser.country
                      ? `${selectedUser.city}, ${selectedUser.region}, ${selectedUser.country}`
                      : selectedUser.city && selectedUser.country
                      ? `${selectedUser.city}, ${selectedUser.country}`
                      : selectedUser.country || "No location information"}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Posts</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {selectedUser.posts && selectedUser.posts > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        <Badge key={selectedUser.posts} variant="blue">
                          Post #{selectedUser.posts}
                        </Badge>
                      </div>
                    ) : (
                      "No posts"
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit User"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button className="ml-3" onClick={handleSaveUser}>
              Save Changes
            </Button>
          </>
        }
      >
        {selectedUser && (
          <form onSubmit={handleSaveUser}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.country}
                    onChange={(e) =>
                      setFormData({ ...formData, country: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Region
                </label>
                <input
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.region}
                  onChange={(e) =>
                    setFormData({ ...formData, region: e.target.value })
                  }
                />
              </div>

              <div className="flex items-center">
                <input
                  id="disabled"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={formData.disabled}
                  onChange={(e) =>
                    setFormData({ ...formData, disabled: e.target.checked })
                  }
                />
                <label
                  htmlFor="disabled"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Disabled
                </label>
              </div>

              {selectedUser.is_admin && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <div className="flex items-center">
                    <input
                      id="is_admin"
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={selectedUser.is_admin}
                      disabled
                    />
                    <label
                      htmlFor="is_admin"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Admin Status (cannot be modified)
                    </label>
                  </div>
                </div>
              )}
            </div>
          </form>
        )}
      </Modal>

      {/* Delete User Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete User"
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="ml-3"
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-500">
          Are you sure you want to delete the user "{selectedUser?.email}"? This
          action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default Users;
